import { Box, Link, Typography } from '@mui/material';
import appStoreBadge from '~/assets/images/app-promo/app-store-badge.png';
import googlePlayBadge from '~/assets/images/app-promo/google-play-badge.png';
import handIphone from '~/assets/images/app-promo/hand-iphone.png';
import { neutral, purpleBrand } from '~/styles/tokens/primitives.colors';
import { radius } from '~/styles/tokens/shape';

export const DEFAULT_APP_STORE_URL = 'https://apps.apple.com/br/app/aride/id6474380000';
export const DEFAULT_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.aride.app';

type JourneyAppPromoProps = {
  appId: string;
  /** Links das lojas (configuráveis; o padrão são os do app Aride). */
  appStoreUrl?: string;
  playStoreUrl?: string;
  /** Foto da mão com o celular à direita (padrão: asset do Figma 1680:7442). `null` esconde a foto. */
  phoneImageSrc?: string | null;
  phoneImageAlt?: string;
};

/** Selo oficial da loja (imagem do Figma, 148×44) como link. */
function StoreBadge({ appId, href, src, label }: { appId: string; href: string; src: string; label: string }) {
  return (
    <Link
      aria-label={label}
      data-app-id={appId}
      href={href}
      rel="noopener noreferrer"
      sx={{
        borderRadius: '8px',
        display: 'inline-flex',
        flexShrink: 0,
        transition: 'opacity 0.2s',
        '&:hover, &:focus-visible': { opacity: 0.85 }
      }}
      target="_blank"
      underline="none"
    >
      <Box alt="" component="img" src={src} sx={{ display: 'block', height: 44, width: 148, objectFit: 'contain' }} />
    </Link>
  );
}

/**
 * «Baixe o aplicativo…» das telas de conclusão (Figma «E-commerce — 2026»). Desktop (1680:7442): texto e selos
 * App Store / Google Play à esquerda, foto da mão com o celular à direita. Celular (2411:2649): texto em cima e,
 * embaixo, os selos empilhados com a foto encostada no canto inferior direito.
 * Ids: `{appId}-app-promo-title`, `{appId}-app-promo`, `{appId}-app-promo-stores`, `{appId}-app-store`,
 * `{appId}-play-store`, `{appId}-app-promo-image`.
 *
 * Cores fixas (primitivas) nos dois modos: o banner é uma peça de marca sobre fundo lilás claro.
 */
export default function JourneyAppPromo({
  appId,
  appStoreUrl = DEFAULT_APP_STORE_URL,
  playStoreUrl = DEFAULT_PLAY_STORE_URL,
  phoneImageSrc = handIphone,
  phoneImageAlt = 'Aplicativo Aride no celular'
}: JourneyAppPromoProps) {
  const hasPhone = Boolean(phoneImageSrc);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'center' }}>
      {/* Roxo do tema nos dois modos (`primary.main`: 700 no claro, 500 no escuro), não a cor de texto padrão. */}
      <Typography data-app-id={`${appId}-app-promo-title`} sx={{ color: 'primary.main' }} variant="h4">
        Baixe o aplicativo e tenha acesso à melhor experiência da plataforma.
      </Typography>
      <Box
        data-app-id={`${appId}-app-promo`}
        sx={{
          bgcolor: purpleBrand[100],
          borderRadius: `${radius.large}px`,
          columnGap: { md: 3, xs: '10px' },
          display: 'grid',
          // Desktop (Figma 1680:7442): texto e selos à esquerda, foto à direita ocupando a altura toda.
          // Celular (Figma 2411:2649): texto em cima; embaixo, selos empilhados e a foto encostada no canto.
          gridTemplateAreas: hasPhone
            ? { md: '"text phone" "badges phone"', xs: '"text text" "badges phone"' }
            : '"text" "badges"',
          gridTemplateColumns: hasPhone ? { md: '1fr auto', xs: '148px 1fr' } : '1fr',
          gridTemplateRows: { md: '1fr 1fr', xs: 'auto auto' },
          overflow: 'hidden',
          pl: 3,
          textAlign: 'left'
        }}
      >
        <Box
          sx={{
            alignSelf: { md: 'end', xs: 'start' },
            display: 'flex',
            flexDirection: 'column',
            gap: { md: '17px', xs: 2 },
            gridArea: 'text',
            minWidth: 0,
            pr: { md: hasPhone ? 0 : 3, xs: 3 },
            pt: 3
          }}
        >
          {/* Só tamanho e altura de linha por breakpoint: `typography: h3/h4` traria a cor do tema (branco/preto). */}
          <Typography
            sx={(t) => ({
              color: purpleBrand[600],
              fontSize: { md: t.typography.h3.fontSize, xs: t.typography.h4.fontSize },
              fontWeight: 600,
              lineHeight: { md: t.typography.h3.lineHeight, xs: t.typography.h4.lineHeight }
            })}
            variant="h3"
          >
            Sua mobilidade protegida em um só app.
          </Typography>
          <Typography sx={{ color: neutral[1100] }} variant="subtitle2">
            Gerencie contratos, pagamentos e benefícios da sua bike, bike elétrica, autopropelido ou ciclomotor.
          </Typography>
        </Box>
        <Box
          data-app-id={`${appId}-app-promo-stores`}
          sx={{
            alignSelf: { md: 'start', xs: 'center' },
            display: 'flex',
            flexDirection: { md: 'row', xs: hasPhone ? 'column' : 'row' },
            flexWrap: 'wrap',
            gap: { md: '35px', xs: '12px' },
            gridArea: 'badges',
            pb: 3,
            pt: { md: '17px', xs: 3 }
          }}
        >
          <StoreBadge
            appId={`${appId}-app-store`}
            href={appStoreUrl}
            label="Disponível na App Store"
            src={appStoreBadge}
          />
          <StoreBadge
            appId={`${appId}-play-store`}
            href={playStoreUrl}
            label="Disponível no Google Play"
            src={googlePlayBadge}
          />
        </Box>
        {phoneImageSrc ? (
          // Mesmo recorte do Figma: a foto original é maior que o slot (160:233; 176×256 no celular) e fica
          // deslocada dentro dele.
          <Box
            data-app-id={`${appId}-app-promo-image`}
            sx={{
              alignSelf: { md: 'stretch', xs: 'end' },
              aspectRatio: '160 / 233',
              borderBottomRightRadius: `${radius.large}px`,
              gridArea: 'phone',
              height: { md: 'auto', xs: 256 },
              justifySelf: 'end',
              minHeight: { md: 233 },
              mt: { md: 0, xs: 3 },
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box
              alt={phoneImageAlt}
              component="img"
              src={phoneImageSrc}
              sx={{
                height: '115.75%',
                left: '-78.14%',
                maxWidth: 'none',
                position: 'absolute',
                top: '-15.75%',
                width: '223.98%'
              }}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
